import React, { useState } from "react";
import { createPortal } from "react-dom";
import PropTypes from "prop-types";

import Modal from "./Modal";
import CrearGrupo from "../Configuracion/Grupos/Create";
import { buscarEnArbol, filtrarArbol, rutaCategoria } from "../../utils/categorias";

/**
 * Árbol de categorías con checkboxes, al estilo del metabox de WordPress.
 *
 * El orden de `valor` importa: la primera categoría es la principal (la que el
 * backend guarda en `productos.grupo_id`). Al marcar una categoría se agrega al
 * final; el botón "Hacer principal" (visible al pasar el mouse) la mueve al frente.
 *
 * Marcar un padre NO marca sus hijos: la asignación es explícita. Lo que sí es
 * jerárquico es el filtrado, donde el backend expande los descendientes.
 *
 * Si se pasa `nombreCampo`, emite un hidden input por categoría para que el
 * `new FormData(form)` de los formularios de producto los recoja como `categorias[]`.
 */
const CategoriasCheckboxTree = ({
	arbol = [],
	valor = [],
	onChange,
	recargarArbol = null,
	nombreCampo = null,
	emitirVacio = false,
	disabled = false,
	alturaMaxima = 240,
	buscable = true,
}) => {
	const [mostrarCrear, setMostrarCrear] = useState(false);
	const [busqueda, setBusqueda] = useState("");

	const principal = buscarEnArbol(arbol, valor[0]);

	const alternar = (id) => {
		onChange(
			valor.includes(id)
				? valor.filter((actual) => actual !== id)
				: [...valor, id]
		);
	};

	const hacerPrincipal = (id) => {
		onChange([id, ...valor.filter((actual) => actual !== id)]);
	};

	const toggleCrear = () => setMostrarCrear((mostrar) => !mostrar);

	const alCrear = (nueva) => {
		if (nueva?.id) onChange([...valor, nueva.id]);
	};

	const renderNodos = (categorias) => (
		<ul className="categorias-arbol__lista">
			{categorias.map((categoria) => {
				const marcada = valor.includes(categoria.id);
				const esPrincipal = valor[0] === categoria.id;

				return (
					<li key={categoria.id}>
						<div className="categorias-arbol__item">
							<label
								className="categorias-arbol__label"
								htmlFor={`categoria-${categoria.id}`}
							>
								<input
									type="checkbox"
									id={`categoria-${categoria.id}`}
									checked={marcada}
									disabled={disabled}
									onChange={() => alternar(categoria.id)}
								/>
								<span className="categorias-arbol__nombre">
									{categoria.nombre}
								</span>
							</label>
							{esPrincipal && (
								<span className="badge badge-primary">Principal</span>
							)}
							{marcada && !esPrincipal && !disabled && (
								<button
									type="button"
									className="categorias-arbol__principal"
									onClick={() => hacerPrincipal(categoria.id)}
								>
									Hacer principal
								</button>
							)}
						</div>
						{categoria.hijos?.length > 0 && renderNodos(categoria.hijos)}
					</li>
				);
			})}
		</ul>
	);

	// Filtrar sólo cambia lo que se ve: los hidden inputs salen de `valor`, así
	// que las marcadas que quedan ocultas se siguen enviando.
	const arbolVisible = filtrarArbol(arbol, busqueda);

	return (
		<>
			{buscable && arbol.length > 0 && (
				<div className="input-group input-group-sm mb-2">
					<div className="input-group-prepend">
						<span className="input-group-text">
							<i className="fas fa-search" aria-hidden="true" />
						</span>
					</div>
					<input
						type="search"
						className="form-control"
						placeholder="Buscar categoría…"
						aria-label="Buscar categoría"
						value={busqueda}
						onChange={(e) => setBusqueda(e.target.value)}
						// Enter no debe enviar el formulario del producto.
						onKeyDown={(e) => e.key === "Enter" && e.preventDefault()}
					/>
				</div>
			)}
			<div
				className={`categorias-arbol ${
					disabled ? "categorias-arbol--disabled" : ""
				}`}
				style={{ maxHeight: `${alturaMaxima}px` }}
			>
				{arbol.length === 0 ? (
					<small className="text-muted">
						Todavía no hay categorías creadas.
					</small>
				) : arbolVisible.length > 0 ? (
					renderNodos(arbolVisible)
				) : (
					<small className="text-muted">
						Ninguna categoría coincide con "{busqueda}".
					</small>
				)}
			</div>

			<div className="categorias-arbol__pie">
				{!disabled && (
					<button
						type="button"
						className="btn btn-link p-0"
						onClick={toggleCrear}
					>
						<i className="fas fa-plus" /> Añadir nueva categoría
					</button>
				)}
				{/* La principal puede quedar fuera de la vista al hacer scroll. */}
				{principal && (
					<small className="text-muted text-right">
						Principal: {rutaCategoria(principal)}
					</small>
				)}
			</div>

			{nombreCampo &&
				valor.map((id) => (
					<input key={id} type="hidden" name={nombreCampo} value={id} />
				))}

			{/*
			 * En multipart no existe forma de expresar un arreglo vacío: si no se
			 * envía ningún `categorias[]`, el backend interpreta que las categorías
			 * no se tocan. Este centinela deja el "quitar todas" expresado en el
			 * request; requiere el soporte correspondiente en ProductoController.
			 */}
			{nombreCampo && emitirVacio && valor.length === 0 && (
				<input type="hidden" name="categorias_vacias" value="1" />
			)}

			{/*
			 * El popup de crear categorías es el mismo del ABM de configuración, así
			 * que desde aquí se puede crear una subcategoría sin salir del producto.
			 *
			 * Va por portal porque este árbol se renderiza dentro del <form> del
			 * producto y el popup trae su propio <form>: anidarlos es HTML inválido
			 * y el navegador termina submitiendo el del producto. Los hidden de
			 * `categorias[]` quedan fuera del portal, así que el FormData del
			 * producto los sigue recogiendo.
			 */}
			{createPortal(
				<Modal show={mostrarCrear} blank={true} toggle={toggleCrear}>
					<CrearGrupo
						toggle={toggleCrear}
						grupo={{}}
						arbol={arbol}
						getGrupos={recargarArbol || (() => {})}
						onCreado={alCrear}
					/>
				</Modal>,
				document.body
			)}
		</>
	);
};

CategoriasCheckboxTree.propTypes = {
	arbol: PropTypes.array,
	valor: PropTypes.array,
	onChange: PropTypes.func.isRequired,
	recargarArbol: PropTypes.func,
	nombreCampo: PropTypes.string,
	emitirVacio: PropTypes.bool,
	disabled: PropTypes.bool,
	alturaMaxima: PropTypes.number,
	buscable: PropTypes.bool,
};

export default CategoriasCheckboxTree;
